'use client';

import { useState, useEffect, useRef } from 'react'
import { Menu, X, Moon, Sun, ChevronDown, Home, Info, Briefcase, Image, MessageCircle, Mail, Sparkles } from 'lucide-react'
import { translations } from '@/lib/translations'
import logo from '../lib/asset/portfolio/logo.jpg'
import { useLocation, useNavigate } from 'react-router-dom'

export default function Header({ isDarkMode, toggleDarkMode, language, toggleLanguage }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')
  const [hoveredItem, setHoveredItem] = useState(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const headerRef = useRef(null)
  const menuRef = useRef(null)
  const t = translations[language]
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (location.pathname.startsWith('/services')) {
      setActiveSection('services')
    } else if (location.pathname.startsWith('/blog')) {
      setActiveSection('blog')
    }
  }, [location.pathname])

  const navItems = [
    { id: 'home', label: t.nav.home, icon: Home },
    { id: 'about', label: t.nav.about, icon: Info },
    { id: 'services', label: t.nav.services, icon: Briefcase },
    { id: 'portfolio', label: t.nav.portfolio, icon: Image },
    { id: 'blog', label: t.nav.blog || 'Blog', icon: Sparkles, href: '/blog' },
    { id: 'testimonials', label: t.nav.testimonials, icon: MessageCircle },
    { id: 'contact', label: t.nav.contact, icon: Mail },
  ]

  // Premium mouse move effect for dynamic gradient
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect()
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        })
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
      
      const sections = navItems.map(item => document.getElementById(item.id))
      const scrollPosition = window.scrollY + 100

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  const scrollToSection = (id, href) => {
    if (href) {
      navigate(href)
      setIsMenuOpen(false)
      return
    }
    if (location.pathname !== '/') {
      navigate(`/#${id}`)
      setIsMenuOpen(false)
      return
    }
    const el = document.getElementById(id)
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' })
    }
    setIsMenuOpen(false)
  }

  // Get background style based on theme and scroll state
  const getHeaderBackground = () => {
    if (isDarkMode) {
      // Dark mode: black blur
      return scrolled 
        ? 'linear-gradient(135deg, rgba(0,0,0,0.95), rgba(0,0,0,0.92))'
        : 'linear-gradient(135deg, rgba(0,0,0,0.85), rgba(0,0,0,0.8))'
    } else {
      // Light mode: white blur
      return scrolled 
        ? 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.92))'
        : 'linear-gradient(135deg, rgba(255,255,255,0.85), rgba(255,255,255,0.8))'
    }
  }

  // Get border color based on theme
  const getBorderColor = () => {
    if (isDarkMode) {
      return scrolled ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent'
    } else {
      return scrolled ? '1px solid rgba(0,0,0,0.1)' : '1px solid transparent'
    }
  }

  // Get box shadow based on theme
  const getBoxShadow = () => {
    if (isDarkMode) {
      return scrolled 
        ? '0 20px 40px -15px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset' 
        : 'none'
    } else {
      return scrolled 
        ? '0 20px 40px -15px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05) inset' 
        : 'none'
    }
  }

  return (
    <>
      <header 
        ref={headerRef}
        className="fixed top-0 inset-x-0 z-50"
        style={{
          background: getHeaderBackground(),
          backdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: getBorderColor(),
          boxShadow: getBoxShadow(),
        }}
      >
        {/* Animated gradient orbs - keep these as they add premium feel */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 blur-[100px] transition-all duration-700"
            style={{
              left: `${mousePosition.x}%`,
              top: `${mousePosition.y}%`,
              transform: 'translate(-50%, -50%)',
              opacity: scrolled ? 0.3 : 0.5,
            }}
          />
          <div 
            className="absolute w-[300px] h-[300px] rounded-full bg-gradient-to-r from-primary/10 to-transparent blur-[80px] transition-all duration-1000"
            style={{
              right: '0%',
              bottom: '0%',
              transform: 'translate(30%, 30%)',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Premium Logo with Crystal Effect */}
            <button type="button" onClick={() => navigate('/')} className="relative group/logo" aria-label="Home">
              {/* Floating orbs */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary via-secondary to-primary rounded-full blur-2xl opacity-0 group-hover/logo:opacity-40 transition-opacity duration-700 animate-pulse" />
              <div className="absolute -inset-2 bg-gradient-to-r from-primary to-secondary rounded-full blur-xl opacity-0 group-hover/logo:opacity-60 transition-opacity duration-500" />
              
              {/* Logo container with crystal effect */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-full blur-md group-hover/logo:blur-lg transition-all duration-500" />
                <img 
                  src={logo} 
                  alt="Logo" 
                  className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-white/20 group-hover/logo:border-white/40 transition-all duration-500 transform group-hover/logo:scale-110 group-hover/logo:rotate-6 shadow-2xl"
                  style={{
                    boxShadow: isDarkMode 
                      ? '0 10px 30px -10px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.1) inset'
                      : '0 10px 30px -10px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.5) inset',
                  }}
                />
              </div>
            </button>

            {/* Desktop Navigation - Premium Glassmorphism */}
            <nav className="hidden md:flex items-center gap-1 lg:gap-2">
              {navItems.map((item, index) => {
                const Icon = item.icon
                const isActive = activeSection === item.id
                const isHovered = hoveredItem === index

                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id, item.href)}
                    onMouseEnter={() => setHoveredItem(index)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className={`
                      relative px-3 lg:px-5 py-2.5 rounded-2xl
                      flex items-center gap-2.5
                      text-sm font-medium
                      transition-all duration-500
                      group/btn
                      overflow-hidden
                      ${isActive 
                        ? 'text-primary' 
                        : isDarkMode 
                          ? 'text-white/70 hover:text-white' 
                          : 'text-gray-700/70 hover:text-gray-900'
                      }
                    `}
                  >
                    {/* Animated background with glass effect */}
                    <div className={`
                      absolute inset-0 rounded-2xl
                      transition-all duration-500
                      ${isActive 
                        ? 'bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 backdrop-blur-sm' 
                        : 'bg-transparent group-hover/btn:bg-gradient-to-r group-hover/btn:from-primary/10 group-hover/btn:via-secondary/5 group-hover/btn:to-transparent'
                      }
                    `} />
                    
                    {/* Shine effect on hover */}
                    <div className={`
                      absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-700
                      bg-gradient-to-r from-transparent via-white/10 to-transparent
                      -translate-x-full group-hover/btn:translate-x-full
                    `} />

                    {/* Icon with premium animation */}
                    <div className={`
                      relative transition-all duration-500
                      ${isActive ? 'text-primary scale-110' : isDarkMode ? 'text-white/50' : 'text-gray-600/50'}
                      ${isHovered ? 'scale-125 -translate-y-1 rotate-3' : ''}
                    `}>
                      <Icon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                    </div>

                    {/* Label with gradient on active */}
                    <span className={`
                      relative
                      ${isActive ? 'bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-semibold' : ''}
                    `}>
                      {item.label}
                    </span>

                    {/* Premium active indicator */}
                    {isActive && (
                      <>
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary to-secondary animate-pulse shadow-lg shadow-primary/50" />
                        </div>
                        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent" />
                      </>
                    )}
                  </button>
                )
              })}
            </nav>

            {/* Desktop Actions - Premium Styled */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language Toggle with 3D effect */}
              <button
                onClick={toggleLanguage}
                className={`
                  relative px-5 py-2.5 rounded-2xl
                  bg-gradient-to-r from-primary/10 to-secondary/10
                  hover:from-primary/20 hover:to-secondary/20
                  border border-primary/20
                  text-sm font-medium
                  transition-all duration-500
                  group/lang
                  overflow-hidden
                  transform hover:scale-105 hover:-translate-y-0.5
                  shadow-lg hover:shadow-primary/20
                  ${isDarkMode ? 'text-white/90' : 'text-gray-900/90'}
                `}
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-hover/lang:opacity-100 transition-opacity duration-500" />
                
                {/* Shine effect */}
                <div className="absolute inset-0 opacity-0 group-hover/lang:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/lang:translate-x-full transition-transform duration-1000" />
                </div>

                <span className="relative z-10 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs shadow-lg">
                    {language === 'en' ? '🇺🇸' : '🇪🇹'}
                  </span>
                  <span className="font-semibold">{language === 'en' ? 'ENG' : 'አማ'}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-primary/70 group-hover/lang:translate-y-1 transition-transform duration-300" />
                </span>
              </button>
              {/* Dark Mode Toggle with cosmic animation */}
              <button
                onClick={toggleDarkMode}
                className={`
                  relative p-3 rounded-2xl
                  bg-gradient-to-br from-muted to-muted/80
                  hover:from-primary/20 hover:to-secondary/20
                  border border-border/50
                  transition-all duration-500
                  group/dark
                  overflow-hidden
                  transform hover:scale-110 hover:rotate-3
                  shadow-lg hover:shadow-primary/20
                `}
              >
                {/* Cosmic particles */}
                <div className="absolute inset-0 opacity-0 group-hover/dark:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 left-1/4 w-1 h-1 bg-yellow-500 rounded-full animate-ping" />
                  <div className="absolute bottom-0 right-1/4 w-1.5 h-1.5 bg-indigo-500 rounded-full animate-ping delay-300" />
                </div>

                <div className="relative z-10 transform group-hover/dark:rotate-180 transition-transform duration-700">
                  {isDarkMode ? (
                    <Sun size={20} className="text-yellow-500 group-hover/dark:scale-125 transition-transform duration-500" />
                  ) : (
                    <Moon size={20} className="text-indigo-500 group-hover/dark:scale-125 transition-transform duration-500" />
                  )}
                </div>
              </button>

             
            </div>

            {/* Mobile Menu Button - Premium */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className={`
                md:hidden relative p-3 rounded-2xl
               
                hover:from-primary/30 hover:to-secondary/30
               
                ${isDarkMode ? 'text-white' : 'text-gray-900'}
              `}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
            >
              {/* Pulse effect */}
              <div className="absolute inset-0 animate-pulse  rounded-2xl" />
              
              <Menu size={22} className="relative z-10 group-hover:scale-110 transition-transform duration-300" />
              
              {/* Glow effect */}
            </button>
          </div>
        </div>
      </header>

      {/* Rest of the component remains exactly the same */}
      {/* Premium Backdrop with blur and gradient */}
      <div
        onClick={() => setIsMenuOpen(false)}
        className={`
          fixed inset-0 z-40
          bg-gradient-to-br from-black/80 via-black/70 to-black/80
          backdrop-blur-xl
          transition-all duration-500
          md:hidden
          ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
        `}
      />

      {/* Mobile Sidebar - Ultra Premium */}
     {/* MOBILE SIDEBAR - VIVID & CENTERED */}
<div 
  className={`fixed inset-0 z-[60] md:hidden transition-all duration-500 ${
    isMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
  }`}
>
  {/* High-Contrast Backdrop */}
  <div 
    className="absolute inset-0 bg-background/98 backdrop-blur-xl"
    onClick={() => setIsMenuOpen(false)}
  />

  {/* Sidebar Content */}
  <div 
    className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
      isMenuOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-10 opacity-0'
    }`}
  >
    {/* Sharp Close Icon - Positioned at Top */}
    <button 
      onClick={() => setIsMenuOpen(false)}
      className="absolute top-12 p-4 text-foreground hover:text-primary transition-all duration-300 active:scale-90"
      aria-label="Close Menu"
    >
      <X size={36} strokeWidth={2} />
    </button>

    {/* Navigation - Centered & Full Contrast */}
    <nav className="flex flex-col items-center gap-10">
      {navItems.map((item, index) => {
        const isActive = activeSection === item.id;
        return (
          <button
            key={item.id}
            onClick={() => scrollToSection(item.id, item.href)}
            style={{ 
              transitionDelay: isMenuOpen ? `${index * 50 + 150}ms` : '0ms'
            }}
            className={`transition-all duration-500 ${
              isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <span className={`text-3xl font-bold tracking-tight transition-all duration-300 ${
              isActive 
              ? 'text-primary' 
              : 'text-foreground hover:text-primary/80'
            }`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>

    {/* Functional Footer - Bold Toggles */}
    <div className="absolute bottom-16 flex items-center gap-12">
      <button 
        onClick={toggleLanguage}
        className="text-xs font-black tracking-[0.2em] text-foreground border-b-2 border-primary pb-1"
      >
        {language === 'en' ? 'አማርኛ' : 'English'}
      </button>
      
      <div className="w-px h-6 bg-foreground/20" />
      
      <button 
        onClick={toggleDarkMode}
        className="text-foreground hover:text-primary transition-transform active:rotate-45"
      >
        {isDarkMode ? <Sun size={22} strokeWidth={2} /> : <Moon size={22} strokeWidth={2} />}
      </button>
    </div>
  </div>
</div>

      <style jsx>{`
        @keyframes slideInFancy {
          0% {
            opacity: 0;
            transform: translateX(-30px) scale(0.9);
          }
          50% {
            opacity: 0.5;
            transform: translateX(5px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar for sidebar */
        .overflow-y-auto::-webkit-scrollbar {
          width: 3px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, hsl(var(--primary)), hsl(var(--secondary)));
          border-radius: 20px;
        }
        
        /* Glassmorphism base */
        .glass {
          backdrop-filter: blur(10px) saturate(180%);
          -webkit-backdrop-filter: blur(10px) saturate(180%);
        }
      `}</style>
    </>
  )
}